import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  guarantors,
  type Guarantor,
  type GuarantorInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDocument,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Avalistas da operacao.
 *
 * Quem avaliza responde solidariamente pela garantia, e e nome por nome que a carta
 * de contragarantia cita. O avalista pertence a esta proposta, e nao ao tomador: a
 * mesma empresa pode apresentar avalistas diferentes em operacoes diferentes.
 *
 * A soma da participacao aparece o tempo todo porque e ela que precisa fechar. Uma
 * soma acima de cem por cento produziria um documento que nao se sustenta, e o
 * servidor recusa antes de gravar.
 */

export interface GuarantorsPanelProps {
  operationId: string;
}

export function GuarantorsPanel({ operationId }: GuarantorsPanelProps) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [criando, setCriando] = useState(false);
  const [emEdicao, setEmEdicao] = useState<Guarantor | null>(null);
  const [removendo, setRemovendo] = useState<Guarantor | null>(null);

  const podeVer = can('operacoes.avalista.view');
  const podeCriar = can('operacoes.avalista.create');
  const podeEditar = can('operacoes.avalista.update');
  const podeRemover = can('operacoes.avalista.delete');

  const consulta = useQuery({
    queryKey: ['guarantors', operationId],
    queryFn: ({ signal }) => guarantors.list(operationId, signal),
    enabled: podeVer,
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['guarantors', operationId] });
  }

  const criar = useMutation({
    mutationFn: (input: GuarantorInput) => guarantors.create(operationId, input),
    onSuccess: () => {
      toast.success('Avalista acrescentado.');
      setCriando(false);
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: GuarantorInput }) =>
      guarantors.update(operationId, id, input),
    onSuccess: () => {
      toast.success('Avalista atualizado.');
      setEmEdicao(null);
      invalidar();
    },
  });

  const remover = useMutation({
    mutationFn: (id: string) => guarantors.remove(operationId, id),
    onSuccess: () => {
      toast.success('Avalista removido.');
      setRemovendo(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) return null;

  const avalistas = consulta.data ?? [];
  const somaBps = avalistas.reduce((total, item) => total + item.participation_bps, 0);

  return (
    <Card>
      <CardHeader
        title="Avalistas"
        description="Quem responde solidariamente pela garantia."
        action={
          podeCriar ? (
            <Button size="sm" variant="secondary" onClick={() => setCriando(true)}>
              <Icon name="sparkle" size={13} />
              Acrescentar
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-3">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os avalistas">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <div className="flex min-h-16 items-center justify-center">
            <Spinner
              size={20}
              label="Carregando avalistas"
              className="text-brand-500"
            />
          </div>
        ) : avalistas.length === 0 ? (
          <p className="text-sm text-ink-500">
            Nenhum avalista nesta operacao. O avalista e opcional, e quando existe passa
            a constar na carta de contragarantia.
          </p>
        ) : (
          <>
            <ul className="flex flex-col divide-y divide-line overflow-hidden rounded-lg border border-line">
              {avalistas.map((avalista) => (
                <li key={avalista.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink-900">
                      {avalista.name}
                    </p>
                    <p className="truncate text-2xs text-ink-500">
                      {avalista.document_kind === 'cnpj' ? 'CNPJ' : 'CPF'}{' '}
                      {formatDocument(avalista.document)}
                    </p>
                  </div>

                  <span className="shrink-0 text-xs tabular-nums text-ink-600">
                    {(avalista.participation_bps / 100).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    %
                  </span>

                  {podeEditar ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Editar ${avalista.name}`}
                      onClick={() => setEmEdicao(avalista)}
                    >
                      <Icon name="copy" size={14} />
                    </Button>
                  ) : null}
                  {podeRemover ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remover ${avalista.name}`}
                      onClick={() => setRemovendo(avalista)}
                    >
                      <Icon name="close" size={14} />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>

            <p className="text-2xs text-ink-500">
              Participacao somada: {(somaBps / 100).toFixed(2)}%
              {somaBps > 10000
                ? ' (acima de cem por cento, o servidor recusa)'
                : somaBps === 10000
                  ? ' (fecha em cem por cento)'
                  : ''}
            </p>
          </>
        )}
      </CardBody>

      {criando ? (
        <FormularioDeAvalista
          titulo="Acrescentar avalista"
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      ) : null}

      {emEdicao ? (
        <FormularioDeAvalista
          titulo={`Editar ${emEdicao.name}`}
          avalista={emEdicao}
          submitting={atualizar.isPending}
          error={atualizar.error}
          onCancel={() => setEmEdicao(null)}
          onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
        />
      ) : null}

      {removendo ? (
        <Modal
          open
          onClose={() => setRemovendo(null)}
          title="Remover avalista"
          description={`${removendo.name} deixa de responder por esta garantia.`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setRemovendo(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={remover.isPending}
                onClick={() => remover.mutate(removendo.id)}
              >
                Remover
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-700">
            A carta de contragarantia gerada a partir daqui deixa de cita-lo. O que ja
            foi assinado nao muda.
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}

function FormularioDeAvalista({
  titulo,
  avalista,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  titulo: string;
  avalista?: Guarantor;
  submitting: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: GuarantorInput) => void;
}) {
  const [name, setName] = useState(avalista?.name ?? '');
  const [document, setDocument] = useState(avalista?.document ?? '');
  const [kind, setKind] = useState(avalista?.document_kind ?? 'cpf');
  const [participation, setParticipation] = useState(
    avalista ? rateToInput(avalista.participation_bps) : '',
  );
  const [email, setEmail] = useState(avalista?.email ?? '');
  const [phone, setPhone] = useState(avalista?.phone ?? '');

  const apiError = error instanceof ApiError ? error : null;
  const erroParticipacao = rateInputError(participation);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (erroParticipacao) return;
    onSubmit({
      name,
      document,
      document_kind: kind,
      participation_bps: rateToBasisPoints(participation || '0'),
      email,
      phone,
    });
  }

  return (
    <Modal open onClose={onCancel} title={titulo} size="md">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {error.message}
          </Alert>
        ) : null}

        <Field
          label="Nome"
          required
          value={name}
          error={fieldErrorOf(apiError, 'name')}
          onChange={(event) => setName(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Tipo de documento"
            value={kind}
            options={[
              { value: 'cpf', label: 'CPF, pessoa fisica' },
              { value: 'cnpj', label: 'CNPJ, pessoa juridica' },
            ]}
            onChange={(event) => setKind(event.target.value)}
          />
          <Field
            label={kind === 'cnpj' ? 'CNPJ' : 'CPF'}
            required
            value={document}
            error={fieldErrorOf(apiError, 'document')}
            onChange={(event) => setDocument(event.target.value)}
          />
        </div>

        <Field
          label="Participacao (%)"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={participation}
          hint="A soma dos avalistas nao passa de cem por cento."
          error={erroParticipacao ?? fieldErrorOf(apiError, 'participation_bps')}
          onChange={(event) => setParticipation(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Email"
            type="email"
            value={email}
            error={fieldErrorOf(apiError, 'email')}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Telefone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={submitting}
            disabled={erroParticipacao !== undefined}
          >
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
